import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type {
  InputMaybe,
  InsertObligationAssessmentResultMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

export interface InsertObligationAssessmentResultTRPCInput {
  Rating: number;
  ObligationIds: string[];
  AssessmentId?: InputMaybe<string>;
  TestDate?: InputMaybe<string>;
  Rationale?: InputMaybe<string>;
  CustomAttributeData?: InputMaybe<CustomAttributeData>;
}

/**
 * Maps TRPC mutation response to match the GraphQL mutation structure
 */
const mapTrpcResponseToGraphQL = (trpcData: {
  Ids: string[];
}): InsertObligationAssessmentResultMutation => {
  return {
    insertChildObligationAssessmentResult: {
      __typename: 'InsertChildObligationAssessmentResultOutput',
      Ids: trpcData.Ids,
    },
  };
};

export const useInsertObligationAssessmentResultTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.obligationAssessmentResult.insert.mutationOptions({
      onSuccess: async () => {
        // Invalidation parity with the legacy Apollo evictFields:
        // obligation_assessment_result (+ aggregate) and assessment.
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.obligationAssessmentResult.pathKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.assessment.pathKey(),
          }),
        ]);
      },
    }),
  });

  return {
    insertObligationAssessmentResult: async (
      variables: InsertObligationAssessmentResultTRPCInput
    ): Promise<InsertObligationAssessmentResultMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
